/**
 * @copyright Microsoft Corporation. All rights reserved.
 *
 * @file Analytics Activity List component
 */

import * as React from 'react';
import { List } from 'office-ui-fabric-react/lib/List';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { AnalyticsActivity, IAnalyticsActivityProps, IAnalyticsActivityStrings } from '../hoverCardActivity/AnalyticsActivity';
import { AnalyticsStats } from '../hoverCardStats/AnalyticsStats';
import strings from '../FileHoverCard.resx';
import * as AnalyticsActivityListStyle from './AnalyticsActivityList.scss';
import { IActivity, IIncompleteData } from '../../../models/ItemAnalytics';

interface IContentLoaderProps {
  onLoaded?: () => void;
}

class ContentLoader extends React.Component<IContentLoaderProps, {}> {
  constructor(props: IContentLoaderProps) {
    super(props);
  }

  public componentDidMount(): void {
    if (this.props.onLoaded) {
      this.props.onLoaded();
    }
  }

  public render(): JSX.Element {
    return <Spinner size={ SpinnerSize.large } />;
  }
}

export interface IAnalyticsActivityListProps {
  viewCount?: number;
  viewerCount?: number;
  isTrending?: boolean;
  activities?: IActivity[];
  inCompleteData?: IIncompleteData;
  moreActivities?: boolean;
  onLoadMoreActivities?: () => void;
  loginUserEmail?: string;
  userEmail?: string;
  showAnalyticsStats?: boolean;
}

export interface IAnalyticsActivityListStrings extends IAnalyticsActivityStrings {
  view: string;
  views: string;
  viewer: string;
  viewers: string;
  inCompleteDataMessage?: string;
  throttledMessage?: string;
}

export class AnalyticsActivityList extends React.Component<IAnalyticsActivityListProps, {}> {
  private _activities: IAnalyticsActivityProps[] = [];

  public componentWillMount(): void {
    this._updateActivities(this.props);
  }

  public componentWillUpdate(nextProps: IAnalyticsActivityListProps): void {
    this._updateActivities(nextProps);
  }

  public render(): JSX.Element {
    const {
      viewCount,
      viewerCount,
      inCompleteData,
      activities,
      showAnalyticsStats = true
    } = this.props;

    if (!!(viewCount && viewerCount) && (this._activities.length > 0)) {
      return (
        <div>
          {
            showAnalyticsStats &&
            <AnalyticsStats
              viewCount={ viewCount }
              viewerCount={ viewerCount }
              activities={ activities }
              inCompleteData={ inCompleteData }
            />
          }
          <List
            items={ this._activities }
            onRenderCell={ this._onRenderCell }
          />
        </div>
      );
    } else {
      return (
        <ContentLoader />
      );
    }
  }

  @autobind
  private _updateActivities(props: IAnalyticsActivityListProps): void {
    this._activities = props.activities ? props.activities.map((activity: IActivity) => {
      return { activity: activity, locStrings: strings };
    }) : [];
  }

  @autobind
  private _onRenderCell(item: IAnalyticsActivityProps, index?: number): JSX.Element {
    return (
      <div className={ `${AnalyticsActivityListStyle.msAnalyticsActivityListActivity}` }>
        <AnalyticsActivity
          activity={ item.activity }
          loginUserEmail={ this.props.loginUserEmail }
          userEmail={ this.props.userEmail }
        />
        {
          this.props.moreActivities && (index === (this._activities.length - 1))
            ? <ContentLoader onLoaded={ this.props.onLoadMoreActivities } />
            : undefined
        }
      </div>
    );
  }
}